// LodgeUI — the Lodge app's look, in one place. Every Lodge page imports its UI
// primitives from here rather than from components/ui, so the Lodge can carry
// the booking-page style (large plain titles, titled section cards, pill
// filters, clickable rows) without changing the Harvus app, which shares
// components/ui. Anything not overridden below is the shared primitive as-is.
// SELF-CONTAINED / REMOVABLE (see OUTFITTER_MODULE.md).
import React from 'react';
import { B, space, font, weight, radius } from '../../../constants/brand';
import { Icon, FieldLabel, Modal, ModalFooter, Btn as UiBtn, Textarea } from '../../../components/ui';
import { useBrand, useAccent } from '../shared/brand';

export * from '../../../components/ui';

// Btn in the outfitter's own colours on a guest page (inside <BrandProvider>);
// the shared button everywhere else. A named export beats the `export *`.
// Darken a #rrggbb by mixing toward black — the hover fill.
const shade = (hex, by = 0.12) => '#' + [1, 3, 5].map((i) => Math.round(parseInt(hex.slice(i, i + 2), 16) * (1 - by)).toString(16).padStart(2, '0')).join('');
export function Btn({ variant = 'primary', style, ...rest }) {
  const b = useBrand();
  if (!b) return <UiBtn variant={variant} style={style} {...rest} />;
  if (variant === 'primary') {
    return <UiBtn variant={variant} hoverBackground={shade(b.primary)} style={{ background: b.primary, backgroundImage: 'none', color: b.onPrimary, ...style }} {...rest} />;
  }
  if (variant === 'ghost') return <UiBtn variant={variant} style={{ color: b.primary, ...style }} {...rest} />;
  if (variant === 'secondary' && !b.console) return <UiBtn variant={variant} hoverBackground={shade(b.secondary)} style={{ background: b.secondary, color: b.onSecondary, ...style }} {...rest} />;
  return <UiBtn variant={variant} style={style} {...rest} />;
}

const CARD_RADIUS = 14;

// Page title: large and plain (no icon tile), subtitle beneath, actions right.
// `icon` is accepted and ignored so existing callers need no change.
// eslint-disable-next-line no-unused-vars
export const PageHeader = ({ title, subtitle, actions, icon, meta }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: space.lg, marginBottom: space.xl, flexWrap: 'wrap' }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap' }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: weight.bold, color: B.text, letterSpacing: -0.5, lineHeight: 1.2 }}>{title}</h1>
        {meta}
      </div>
      {subtitle && <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 4 }}>{subtitle}</div>}
    </div>
    {actions && <div style={{ display: 'flex', alignItems: 'center', gap: space.sm, flexShrink: 0, flexWrap: 'wrap' }}>{actions}</div>}
  </div>
);

// A full-page editor (a booking, a contact): a small back link above a large
// title, status chips beside the title, and the save bar pinned to the bottom
// of the screen so Save never scrolls away on a long form.
export function EditPageFrame({ title, meta, onBack, children, footer, backLabel = 'Back' }) {
  return (
    <div style={{ maxWidth: 1240 }}>
      <button type="button" onClick={onBack} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', padding: 0, marginBottom: space.sm, color: B.textMd, cursor: 'pointer', fontSize: font.sm, fontFamily: 'inherit', fontWeight: weight.medium }}>← {backLabel}</button>
      <div style={{ display: 'flex', alignItems: 'center', gap: space.md, flexWrap: 'wrap', marginBottom: space.lg }}>
        <h1 style={{ margin: 0, fontSize: 26, fontWeight: weight.bold, color: B.text, letterSpacing: -0.5 }}>{title}</h1>
        {meta}
      </div>
      {children}
      {footer && (
        <div style={{ position: 'sticky', bottom: 0, zIndex: 5, display: 'flex', gap: space.sm, justifyContent: 'flex-end', flexWrap: 'wrap', alignItems: 'center',
          background: 'rgba(255,255,255,0.88)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
          border: `1px solid ${B.border}`, borderRadius: radius.xl2 || 14, padding: `${space.sm}px ${space.md}px`, boxShadow: B.shadowMd || B.shadowSm, marginTop: space.sm }}>
          {footer}
        </div>
      )}
    </div>
  );
}

// useConfirm — the Lodge's own "Are you sure?" instead of the browser's
// window.confirm / window.prompt. `ask(opts)` opens a small dialog and resolves
// true (or, with `reason`, the typed reason) on confirm, false on cancel.
//   opts: { title, message, confirmLabel = 'Confirm', danger = false,
//           reason: { label, placeholder } }   // optional text box
// Render the returned element anywhere in the component.
export function useConfirm() {
  const [req, setReq] = React.useState(null);
  const [text, setText] = React.useState('');
  const ask = React.useCallback((opts) => new Promise((resolve) => { setText(''); setReq({ ...opts, resolve }); }), []);
  const close = (v) => { if (req) req.resolve(v); setReq(null); };
  const element = req ? (
    <Modal title={req.title || 'Are you sure?'} onClose={() => close(false)} width={460}>
      {req.message && <div style={{ fontSize: font.sm, color: B.text, marginBottom: space.md }}>{req.message}</div>}
      {req.reason && <Textarea label={req.reason.label || 'Reason'} value={text} onChange={setText} rows={3} placeholder={req.reason.placeholder || ''} />}
      <ModalFooter>
        <Btn variant="ghost" onClick={() => close(false)}>Cancel</Btn>
        <Btn variant={req.danger ? 'danger' : 'primary'} onClick={() => close(req.reason ? text : true)}>{req.confirmLabel || 'Confirm'}</Btn>
      </ModalFooter>
    </Modal>
  ) : null;
  return [ask, element];
}

// A tinted warning box: amber for "check this", red for "this could hurt
// someone" (food allergies). One shape for every Lodge warning.
export function Callout({ tone = B.amber, role, title, children, style }) {
  return (
    <div role={role} style={{ background: `${tone}12`, border: `1px solid ${tone}55`, borderRadius: radius.lg, padding: '10px 14px', marginBottom: space.md, fontSize: font.sm, color: B.text, ...style }}>
      {title && <div style={{ fontWeight: weight.bold, color: tone, marginBottom: 4 }}>{title}</div>}
      {children}
    </div>
  );
}

// A small outlined flag beside a name (★ VIP, Needs review) — one shape for
// every Lodge screen.
export function FlagBadge({ children, title, color = B.amber, style }) {
  return (
    <span title={title} style={{ display: 'inline-flex', alignItems: 'center', marginLeft: 6, fontSize: 10, fontWeight: weight.bold, color,
      border: `1px solid ${color}`, borderRadius: 999, padding: '0 6px', verticalAlign: 'middle', whiteSpace: 'nowrap', lineHeight: '16px', ...style }}>{children}</span>
  );
}
export function VipBadge({ style }) {
  return <FlagBadge title="VIP" style={style}>★ VIP</FlagBadge>;
}

// A reservation / confirmation code, drawn the same way on every screen.
export function CodeChip({ code, size = 'md' }) {
  if (!code) return null;
  return (
    <span style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: size === 'sm' ? font.xs : font.sm, color: B.navy,
      border: `1px solid ${B.navy}44`, background: B.navy + '0d', borderRadius: 999, padding: size === 'sm' ? '1px 8px' : '3px 10px',
      fontWeight: weight.semibold, whiteSpace: 'nowrap' }}>{code}</span>
  );
}

// A card: 14px corners, a hairline border, a soft shadow and tighter padding.
export const Card = ({ children, style = {}, interactive = false, ...rest }) => (
  <div {...rest}
    style={{
      background: B.surface, borderRadius: CARD_RADIUS, border: `1px solid ${B.border}`,
      boxShadow: B.shadowXs || B.shadowSm, padding: space.lg,
      transition: interactive ? 'box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease' : undefined,
      ...style,
    }}
    onMouseEnter={interactive ? (e) => { e.currentTarget.style.boxShadow = B.shadowMd; e.currentTarget.style.transform = 'translateY(-1px)'; e.currentTarget.style.borderColor = B.navy + '55'; } : undefined}
    onMouseLeave={interactive ? (e) => { e.currentTarget.style.boxShadow = B.shadowXs || B.shadowSm; e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.borderColor = B.border; } : undefined}
  >{children}</div>
);

// A titled card — the unit every Lodge page is built from.
export function Section({ title, subtitle, action, children, style }) {
  return (
    <Card style={{ marginBottom: space.lg, padding: `${space.xl}px ${space.xl}px ${space.lg}px`, border: `1px solid ${B.divider}`, ...style }}>
      {(title || action) && (
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: space.md, marginBottom: space.lg }}>
          <div style={{ minWidth: 0 }}>
            {title && <h2 style={{ margin: 0, fontSize: font.lg, fontWeight: weight.semibold, color: B.text, letterSpacing: -0.2 }}>{title}</h2>}
            {subtitle && <div style={{ fontSize: font.base, color: B.textMd, marginTop: 2 }}>{subtitle}</div>}
          </div>
          {action}
        </div>
      )}
      {children}
    </Card>
  );
}

// A heading over content that already draws its own card — a Table, or a list
// of Cards. Anything else (a form group, free text) is a Section, so a card
// never sits inside a card and a heading never sits inside a hand-built one.
export const SectionHeader = ({ title, subtitle, action }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: space.sm, marginBottom: space.md, marginTop: space.sm }}>
    <div>
      <div style={{ fontWeight: weight.bold, fontSize: font.md, color: B.text, letterSpacing: -0.2 }}>{title}</div>
      {subtitle && <div style={{ fontSize: font.xs, color: B.textMd, marginTop: 2 }}>{subtitle}</div>}
    </div>
    {action}
  </div>
);

// A table in a card. A clickable row gets a hover fill and a › on the right,
// so "click to open" reads without an Edit button.
// `reorder` (optional) makes rows draggable: { keyOf(row), canDrop?(from, to),
// onDrop(fromRow, toRow, after) }. A line above or below the target row shows
// where the dragged row lands. Mouse drags the whole row; touch/pen drags from
// an element marked `data-drag-handle`. Off by default, so other tables are unchanged.
// The nearest ancestor that scrolls vertically (a modal body), else null = window.
function scrollParentOf(el) {
  for (let n = el && el.parentElement; n && n !== document.body; n = n.parentElement) {
    const oy = window.getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight) return n;
  }
  return null;
}

export const Table = ({ columns, data, onRowClick, rowFilter, empty = 'Nothing here yet', reorder }) => {
  const rows = rowFilter ? (data || []).filter(rowFilter) : (data || []);
  const [drag, setDrag] = React.useState(null); // { from, over, after }
  const dragKey = (row) => (reorder ? String(reorder.keyOf(row)) : null);
  const rowOf = (k) => rows.find((r) => dragKey(r) === k);
  const allowed = (fromKey, row) => {
    const from = rowOf(fromKey);
    return !!from && dragKey(row) !== fromKey && (!reorder.canDrop || reorder.canDrop(from, row));
  };
  const dragProps = (row) => {
    if (!reorder) return {};
    const k = dragKey(row);
    return {
      draggable: true,
      onDragStart: (e) => { e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', k); } catch { /* some browsers refuse */ } setDrag({ from: k, over: null, after: false }); },
      onDragOver: (e) => {
        if (!drag || !allowed(drag.from, row)) return;
        e.preventDefault(); e.dataTransfer.dropEffect = 'move';
        const box = e.currentTarget.getBoundingClientRect();
        const after = e.clientY > box.top + box.height / 2;
        if (drag.over !== k || drag.after !== after) setDrag({ ...drag, over: k, after });
      },
      onDrop: (e) => {
        e.preventDefault();
        if (drag && allowed(drag.from, row)) reorder.onDrop(rowOf(drag.from), row, drag.after);
        setDrag(null);
      },
      onDragEnd: () => setDrag(null),
    };
  };
  // Touch / pen (iPad): HTML5 drag events do not fire for a finger, so a press
  // on an element marked `data-drag-handle` inside a row drags by pointer
  // events instead. Only the handle starts it, so the page still scrolls under
  // a finger everywhere else. The row under the finger is found with
  // elementFromPoint; the nearest scrollable box scrolls near its edges.
  const dragRef = React.useRef(null);
  const reorderRef = React.useRef(reorder); reorderRef.current = reorder;
  const rowsRef = React.useRef(rows); rowsRef.current = rows;
  const setDragBoth = (v) => { dragRef.current = v; setDrag(v); };
  const onTouchStart = (e) => {
    if (!reorder || e.pointerType === 'mouse') return;
    const handle = e.target.closest && e.target.closest('[data-drag-handle]');
    const tr = handle && handle.closest('tr[data-reorder-key]');
    if (!tr) return;
    e.preventDefault();
    const from = tr.getAttribute('data-reorder-key');
    setDragBoth({ from, over: null, after: false });
    const scroller = scrollParentOf(tr);
    const move = (ev) => {
      ev.preventDefault();
      const cur = dragRef.current; if (!cur) return;
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const over = el && el.closest && el.closest('tr[data-reorder-key]');
      const k = over && over.getAttribute('data-reorder-key');
      const target = k && rowsRef.current.find((r) => String(reorderRef.current.keyOf(r)) === k);
      const fromRow = rowsRef.current.find((r) => String(reorderRef.current.keyOf(r)) === cur.from);
      const ok = target && fromRow && k !== cur.from && (!reorderRef.current.canDrop || reorderRef.current.canDrop(fromRow, target));
      if (ok) {
        const box = over.getBoundingClientRect();
        const after = ev.clientY > box.top + box.height / 2;
        if (cur.over !== k || cur.after !== after) setDragBoth({ ...cur, over: k, after });
      } else if (cur.over) setDragBoth({ ...cur, over: null });
      const edge = 48;
      const box = scroller ? scroller.getBoundingClientRect() : { top: 0, bottom: window.innerHeight };
      const by = ev.clientY < box.top + edge ? -12 : ev.clientY > box.bottom - edge ? 12 : 0;
      if (by) { if (scroller) scroller.scrollTop += by; else window.scrollBy(0, by); }
    };
    const end = (ev) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', end); window.removeEventListener('pointercancel', end);
      const cur = dragRef.current;
      setDragBoth(null);
      if (ev.type !== 'pointerup' || !cur || !cur.over) return;
      const find = (k) => rowsRef.current.find((r) => String(reorderRef.current.keyOf(r)) === k);
      const fromRow = find(cur.from); const toRow = find(cur.over);
      if (fromRow && toRow) reorderRef.current.onDrop(fromRow, toRow, cur.after);
    };
    window.addEventListener('pointermove', move, { passive: false });
    window.addEventListener('pointerup', end);
    window.addEventListener('pointercancel', end);
  };
  const dropLine = (row) => {
    if (!drag || drag.over !== dragKey(row)) return {};
    return drag.after ? { boxShadow: `inset 0 -3px 0 ${B.navy}` } : { boxShadow: `inset 0 3px 0 ${B.navy}` };
  };
  const cols = onRowClick && !columns.some((c) => c.key === '__open')
    ? [...columns, { key: '__open', label: '', align: 'right', render: () => <span aria-hidden="true" style={{ color: B.textLt, fontSize: font.lg, lineHeight: 1 }}>›</span> }]
    : columns;
  return (
    <div style={{ overflowX: 'auto', border: `1px solid ${B.border}`, borderRadius: CARD_RADIUS, background: B.surface, boxShadow: B.shadowXs || B.shadowSm }}>
      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: font.base }}>
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.key} scope="col" style={{ padding: '12px 16px', textAlign: c.align || 'left', fontWeight: weight.semibold, color: B.textMd, fontSize: font.xs, letterSpacing: 0.4, textTransform: 'uppercase', borderBottom: `1px solid ${B.border}`, whiteSpace: 'nowrap', background: B.surfaceMuted }}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody onPointerDown={reorder ? onTouchStart : undefined}>
          {rows.map((row, i) => (
            <tr key={reorder ? dragKey(row) : (row.id ?? i)} data-reorder-key={reorder ? dragKey(row) : undefined}
              onClick={onRowClick ? () => onRowClick(row) : undefined} {...dragProps(row)}
              role={onRowClick ? 'button' : undefined} tabIndex={onRowClick ? 0 : undefined}
              onKeyDown={onRowClick ? ((e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onRowClick(row); } }) : undefined}
              style={{ borderBottom: i === rows.length - 1 ? 'none' : `1px solid ${B.divider}`, cursor: onRowClick ? 'pointer' : reorder ? 'grab' : 'default', transition: 'background 0.1s',
                opacity: drag && drag.from === dragKey(row) ? 0.45 : 1, ...dropLine(row) }}
              onMouseEnter={(e) => { e.currentTarget.style.background = B.hover; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = ''; }}>
              {cols.map((c) => (
                <td key={c.key} style={{ padding: '13px 16px', color: B.text, verticalAlign: 'middle', textAlign: c.align || 'left' }}>
                  {c.render ? c.render(row[c.key], row, i) : (row[c.key] ?? '—')}
                </td>
              ))}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={cols.length} style={{ padding: '44px 16px', textAlign: 'center', color: B.textMd }}>{empty}</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
};

// Filter pills with counts: [{ value, label, count? }]. A pill with a zero
// count is hidden unless it is the one selected.
export function FilterPills({ options, value, onChange, label = 'Filter' }) {
  return (
    <div role="tablist" aria-label={label} style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: space.md }}>
      {options.map((o) => {
        const on = value === o.value;
        if (o.count === 0 && !on && o.value) return null;
        return (
          <button key={o.value || 'all'} type="button" role="tab" aria-selected={on} onClick={() => onChange(o.value)} style={{
            display: 'inline-flex', alignItems: 'center', gap: 6, border: `1px solid ${on ? B.navy : B.border}`, background: on ? B.navy : B.surface,
            color: on ? B.white : B.text, borderRadius: 999, padding: '5px 12px', fontSize: font.sm, fontFamily: 'inherit', cursor: 'pointer',
            fontWeight: on ? weight.semibold : weight.medium, transition: 'background 0.12s, color 0.12s',
          }}>
            {o.label}
            {o.count != null && <span style={{ fontSize: font.xs, borderRadius: 999, padding: '0 6px', background: on ? '#ffffff2e' : B.surfaceMuted, color: on ? B.white : B.textMd }}>{o.count}</span>}
          </button>
        );
      })}
    </div>
  );
}

// A segmented toggle ([value, label] pairs) — Month/Week and the like.
export function Segmented({ options, value, onChange, label }) {
  return (
    <div role="tablist" aria-label={label} style={{ display: 'inline-flex', gap: 3, background: B.surfaceMuted, border: `1px solid ${B.divider}`, borderRadius: 999, padding: 3 }}>
      {options.map(([v, l]) => {
        const on = value === v;
        return (
          <button key={v} type="button" role="tab" aria-selected={on} onClick={() => onChange(v)} style={{
            border: 'none', padding: '6px 14px', borderRadius: 999, fontSize: font.sm, fontFamily: 'inherit', cursor: 'pointer',
            background: on ? B.surface : 'transparent', color: on ? B.navyDk || B.navy : B.textMd, boxShadow: on ? (B.shadowXs || B.shadowSm) : 'none',
            fontWeight: on ? weight.semibold : weight.medium,
          }}>{l}</button>
        );
      })}
    </div>
  );
}

export const lodgeRadius = { card: CARD_RADIUS, ...radius };

// ── Direction D: form controls (choice cards, add-on cards, counters,
// segmented choices, a price summary and the save hint). Booking forms build
// from these so every Lodge form reads as one system. Hover/focus lives in
// GlobalStyles (.lodge-card) because inline styles cannot express :hover.
const fmtMoney = (n) => `${Number(n) < 0 ? '−' : ''}$${Math.abs(Number(n || 0)).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
export const lodgeMoney = fmtMoney;

// A small uppercase group label inside a section.
export function GroupLabel({ children, hint, style }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: space.sm, marginBottom: space.sm, ...style }}>
      <span style={{ fontSize: font.xs, fontWeight: weight.semibold, letterSpacing: 0.6, textTransform: 'uppercase', color: B.textMd }}>{children}</span>
      {hint && <span style={{ fontSize: font.sm, color: B.textLt }}>{hint}</span>}
    </div>
  );
}

const cardBase = (on) => ({
  position: 'relative', textAlign: 'left', fontFamily: 'inherit', cursor: 'pointer',
  border: `1px solid ${on ? B.navy : B.border}`, background: on ? B.accentLt : B.surface,
  boxShadow: on ? `0 0 0 1px ${B.navy} inset` : 'none', color: B.text, borderRadius: radius.xxl,
  transition: 'background 0.12s, border-color 0.12s, box-shadow 0.12s',
});

const grid = (min) => ({ display: 'grid', gridTemplateColumns: `repeat(auto-fill, minmax(${min}px, 1fr))`, gap: space.sm });

// options: [{ code, label, sub?, icon?, price? }]. multi=true: value is an array.
export function ChoiceCards({ label, hint, options, value, onChange, multi = false, min = 132, style }) {
  const isOn = (c) => (multi ? (value || []).includes(c) : value === c);
  const toggle = (c) => {
    if (multi) onChange(isOn(c) ? value.filter((x) => x !== c) : [...(value || []), c]);
    else onChange(isOn(c) ? null : c);
  };
  return (
    <div style={{ marginBottom: space.lg, ...style }}>
      {label && <GroupLabel hint={hint}>{label}</GroupLabel>}
      <div style={grid(min)}>
        {options.map((o) => {
          const on = isOn(o.code);
          return (
            <button key={o.code} type="button" className="lodge-card" aria-pressed={on} onClick={() => toggle(o.code)}
              style={{ ...cardBase(on), padding: '10px 12px', display: 'grid', gap: 4, alignContent: 'start', minHeight: o.icon ? 76 : 0 }}>
              {o.icon && <Icon name={o.icon} size={20} color={on ? B.navy : B.textMd} strokeWidth={1.75} />}
              <span style={{ fontSize: font.md, fontWeight: weight.semibold, lineHeight: 1.25 }}>{o.label}</span>
              {(o.sub || o.price != null) && (
                <span style={{ fontSize: font.sm, color: B.textMd }}>{o.price != null ? fmtMoney(o.price) : ''}{o.price != null && o.sub ? ' · ' : ''}{o.sub || ''}</span>
              )}
              {on && <CheckDot style={{ position: 'absolute', top: 10, right: 10 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function CheckDot({ style }) {
  return (
    <span aria-hidden="true" style={{ width: 18, height: 18, borderRadius: 9, background: B.navy, color: B.white, display: 'grid', placeItems: 'center', ...style }}>
      <Icon name="check" size={12} strokeWidth={3} />
    </span>
  );
}

// Add-on cards: a title, a line and a price; a check shows once picked (no
// empty checkbox — the client asked for multi-selects without checkboxes). value is an array of codes.
export function CheckCards({ label, hint, options, value = [], onChange, min = 210, style }) {
  const toggle = (c) => onChange(value.includes(c) ? value.filter((x) => x !== c) : [...value, c]);
  return (
    <div style={{ marginBottom: space.lg, ...style }}>
      {label && <GroupLabel hint={hint}>{label}</GroupLabel>}
      <div style={grid(min)}>
        {options.map((o) => {
          const on = value.includes(o.code);
          return (
            <button key={o.code} type="button" className="lodge-card" role="checkbox" aria-checked={on} onClick={() => toggle(o.code)}
              style={{ ...cardBase(on), padding: '10px 12px', paddingRight: on ? 36 : 12, display: 'flex', gap: 10, alignItems: 'flex-start' }}>
              <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 1 }}>
                <span style={{ fontSize: font.md, fontWeight: weight.semibold }}>{o.label}</span>
                {o.sub && <span style={{ fontSize: font.sm, color: B.textMd }}>{o.sub}</span>}
              </span>
              {o.price != null && <span style={{ fontSize: font.base, fontWeight: weight.semibold, whiteSpace: 'nowrap' }}>{fmtMoney(o.price)}</span>}
              {on && <CheckDot style={{ position: 'absolute', top: 10, right: 10 }} />}
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Several-of a short list (days of the week): the segmented look, but each
// option toggles on its own. value: an array of option values.
export function MultiChoice({ options, value = [], onChange, ariaLabel }) {
  const brand = useBrand();
  const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
  return (
    <span role="group" aria-label={ariaLabel} style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 2, padding: 2, borderRadius: radius.lg + 2, background: B.surfaceSunken }}>
      {options.map((o) => {
        const on = value.includes(o.value);
        return (
          <button key={String(o.value)} type="button" aria-pressed={on} onClick={() => toggle(o.value)} className="lodge-seg" style={{
            border: 'none', borderRadius: radius.lg, padding: '5px 12px', fontSize: font.base, fontFamily: 'inherit', cursor: 'pointer',
            fontWeight: on ? weight.semibold : weight.medium, color: on ? (brand ? brand.onPrimary : B.white) : B.textMd,
            background: on ? (brand ? brand.primary : B.navy) : 'transparent', boxShadow: on ? B.shadowSm : 'none',
          }}>{o.label}</button>
        );
      })}
    </span>
  );
}

// Counted cards: each option carries its own count (0 = off).
export function CountCards({ label, hint, options, countOf, onCount, max = 9, footer, min = 240 }) {
  return (
    <div style={{ marginBottom: space.lg }}>
      {label && <GroupLabel hint={hint}>{label}</GroupLabel>}
      <div style={grid(min)}>
        {options.map((o) => {
          const n = countOf(o);
          const on = n > 0;
          return (
            <div key={o.code} style={{ ...cardBase(on), cursor: 'default', padding: '10px 12px', display: 'flex', alignItems: 'center', gap: space.sm }}>
              {o.icon && <Icon name={o.icon} size={20} color={on ? B.navy : B.textMd} strokeWidth={1.75} />}
              <span style={{ flex: 1, minWidth: 0, display: 'grid', gap: 1 }}>
                <span style={{ fontSize: font.md, fontWeight: weight.semibold }}>{o.label}</span>
                {o.sub && <span style={{ fontSize: font.sm, color: B.textMd }}>{o.sub}</span>}
              </span>
              <Stepper value={n} onChange={(v) => onCount(o, v)} max={max} label={o.label} />
            </div>
          );
        })}
      </div>
      {footer}
    </div>
  );
}

export function Stepper({ value, onChange, min = 0, max = 99, label }) {
  const btn = (disabled) => ({
    width: 28, height: 28, borderRadius: radius.lg, border: `1px solid ${B.border}`, background: B.surface, color: B.text,
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.4 : 1, display: 'grid', placeItems: 'center', fontFamily: 'inherit', padding: 0,
  });
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <button type="button" aria-label={`Fewer ${label}`} disabled={value <= min} onClick={() => onChange(value - 1)} style={btn(value <= min)}>−</button>
      <span aria-live="polite" style={{ minWidth: 18, textAlign: 'center', fontWeight: weight.semibold, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
      <button type="button" aria-label={`More ${label}`} disabled={value >= max} onClick={() => onChange(value + 1)} style={btn(value >= max)}>+</button>
    </span>
  );
}

// A segmented choice for a FORM value (aria-pressed, not tabs).
// options: [{ value, label }]. value may be null (nothing picked).
export function SegmentedChoice({ options, value, onChange, ariaLabel, size = 'md' }) {
  const brand = useBrand();
  const pad = size === 'sm' ? '3px 10px' : '5px 12px';
  return (
    <span role="group" aria-label={ariaLabel} style={{ display: 'inline-flex', flexWrap: 'wrap', gap: 2, padding: 2, borderRadius: radius.lg + 2, background: B.surfaceSunken }}>
      {options.map((o) => {
        const on = value === o.value;
        return (
          <button key={String(o.value)} type="button" aria-pressed={on} onClick={() => onChange(o.value)} className="lodge-seg" style={{
            border: 'none', borderRadius: radius.lg, padding: pad, fontSize: font.base, fontFamily: 'inherit', cursor: 'pointer',
            fontWeight: on ? weight.semibold : weight.medium, color: on ? (brand ? brand.onPrimary : B.white) : B.textMd,
            background: on ? (brand ? brand.primary : B.navy) : 'transparent', boxShadow: on ? B.shadowSm : 'none',
          }}>{o.label}</button>
        );
      })}
    </span>
  );
}

export function SummaryRow({ label, sub, value, strong = false, tone }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: space.md, padding: strong ? '10px 0 0' : '4px 0', alignItems: 'baseline',
      borderTop: strong ? `1px solid ${B.divider}` : 'none', marginTop: strong ? space.sm : 0 }}>
      <span style={{ fontSize: strong ? font.md : font.base, fontWeight: strong ? weight.bold : weight.regular, color: strong ? B.text : B.textMd, minWidth: 0 }}>
        {label}{sub && <span style={{ color: B.textLt }}> {sub}</span>}
      </span>
      <span style={{ fontSize: strong ? font.xl : font.base, fontWeight: strong ? weight.bold : weight.medium, color: tone || B.text, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{value}</span>
    </div>
  );
}

// What still blocks the save. items: string[]. Empty → nothing rendered.
export function MissingHint({ items, style }) {
  if (!items || !items.length) return null;
  return (
    <span role="status" style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: font.base, color: B.amber, fontWeight: weight.medium, ...style }}>
      <Icon name="warning" size={14} />
      {items.length === 1 ? items[0] : `${items[0]} (+${items.length - 1} more)`}
    </span>
  );
}

// A labelled field for a SHORT value (2–4 words each): a segmented control
// sitting where a dropdown would, with the same label and spacing as Input.
export function SegmentedField({ label, options, value, onChange, required, hint }) {
  return (
    <div style={{ marginBottom: space.lg }}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <SegmentedChoice ariaLabel={label} options={options} value={value} onChange={onChange} />
      {hint && <div style={{ fontSize: font.sm, color: B.textMd, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

// A labelled one-of choice whose options need a sentence: radio cards side by
// side. options: [{ value, label, sub? }]. Clicking the picked card keeps it.
export function RadioCards({ label, options, value, onChange, disabled = false, min = 200 }) {
  const accent = useAccent();
  return (
    <div role="radiogroup" aria-label={label} style={{ marginBottom: space.lg }}>
      {label && <FieldLabel>{label}</FieldLabel>}
      <div style={grid(min)}>
        {options.map((o) => {
          const on = value === o.value;
          return (
            <button key={String(o.value)} type="button" role="radio" aria-checked={on} disabled={disabled}
              className="lodge-card" onClick={() => onChange(o.value)}
              style={{ ...cardBase(on), ...(on && accent !== B.navy ? { borderColor: accent, boxShadow: `0 0 0 1px ${accent} inset`, background: B.surface } : null), padding: '10px 12px', display: 'flex', gap: 10, alignItems: 'flex-start', opacity: disabled ? 0.6 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
              <span aria-hidden="true" style={{ flex: 'none', width: 16, height: 16, borderRadius: 8, marginTop: 2,
                border: `1.5px solid ${on ? accent : B.border}`, background: B.surface, display: 'grid', placeItems: 'center' }}>
                {on && <span style={{ width: 8, height: 8, borderRadius: 4, background: accent }} />}
              </span>
              <span style={{ display: 'grid', gap: 1, minWidth: 0 }}>
                <span style={{ fontSize: font.md, fontWeight: weight.semibold }}>{o.label}</span>
                {o.sub && <span style={{ fontSize: font.sm, color: B.textMd }}>{o.sub}</span>}
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// A single on/off setting as a row: the label (and a hint) on the left, a
// switch on the right. Replaces the bare checkbox for a yes/no setting.
export function ToggleRow({ label, hint, checked, onChange, disabled = false, style }) {
  return (
    <label style={{ display: 'flex', alignItems: 'center', gap: space.md, padding: '8px 0', cursor: disabled ? 'not-allowed' : 'pointer', opacity: disabled ? 0.55 : 1, marginBottom: space.sm, ...style }}>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={{ display: 'block', fontSize: font.md, color: B.text }}>{label}</span>
        {hint && <span style={{ display: 'block', fontSize: font.sm, color: B.textMd, marginTop: 1 }}>{hint}</span>}
      </span>
      <Switch checked={checked} onChange={onChange} disabled={disabled} label={typeof label === 'string' ? label : undefined} />
    </label>
  );
}

export function Switch({ checked, onChange, disabled = false, label }) {
  return (
    <span style={{ position: 'relative', flex: 'none', width: 36, height: 20 }}>
      <input type="checkbox" role="switch" aria-label={label} checked={!!checked} disabled={disabled}
        onChange={(e) => onChange(e.target.checked)} className="lodge-switch"
        style={{ position: 'absolute', inset: 0, margin: 0, opacity: 0, cursor: 'inherit' }} />
      <span aria-hidden="true" style={{ position: 'absolute', inset: 0, borderRadius: 10, background: checked ? B.navy : B.border, transition: 'background 0.15s' }} />
      <span aria-hidden="true" style={{ position: 'absolute', top: 2, left: checked ? 18 : 2, width: 16, height: 16, borderRadius: 8, background: B.white, boxShadow: B.shadowSm, transition: 'left 0.15s' }} />
    </span>
  );
}
